import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FaFileAlt, FaStamp, FaClipboardList, FaPlus, FaTrash, FaEdit,
  FaCheck, FaTimes, FaChevronDown, FaChevronUp, FaUser, FaSearch,
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import { decodeToken, isExpired } from 'react-jwt';
import { useNavigate } from 'react-router-dom';

// ─── Tipos de documento (as 3 abas) ──────────────────────────────────────────
const TIPOS = [
  { id: 'MEMORANDO', label: 'Memorandos', icon: FaFileAlt, cor: '#3498db' },
  { id: 'OFICIO', label: 'Ofícios', icon: FaStamp, cor: '#008080' },
  { id: 'EPROD', label: 'EPROD', icon: FaClipboardList, cor: '#9b59b6' },
];

// ── Rota do backend: SEMPRE no singular (oficio-memorando), nunca plural ──
const ROTA_BASE = 'oficio-memorando';

const anoAtual = new Date().getFullYear();

// Ex: OFÍCIO 007/2026 — número sempre com 3 dígitos + ano vigente
const formatarNumeroDoc = (tipoId, numero, ano) => {
  const tipo = TIPOS.find(t => t.id === tipoId);
  const label = tipo ? tipo.label.replace(/S$/, '').toUpperCase() : tipoId; // "Memorandos" -> "MEMORANDO"
  return `${label} ${String(numero).padStart(3, '0')}/${ano || anoAtual}`;
};

const formatarDataHoraBR = (isoDateTime) => {
  if (!isoDateTime) return '';
  const d = new Date(isoDateTime);
  if (isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

// ── Nome de quem criou o registro. O back devolve o campo "nomeUsuarioCadastro",
// mas mantemos alguns fallbacks pra não quebrar caso o formato mude. ──
const nomeCriador = (item) =>
  item?.nomeUsuarioCadastro || item?.usuarioNome || item?.nomeUsuario || item?.criadoPor || item?.usuario?.nome || null;

// ── Data/hora de registro. O back devolve o campo "dataCadastro". ──
const dataRegistro = (item) => item?.dataCadastro || item?.criadoEm || null;

function OficiosMemorandos() {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();
  const navigate = useNavigate();

  // ── Nome do usuário logado — usado pra já exibir "quem criou" no item
  // assim que ele é adicionado, sem esperar o próximo carregamento do backend. ──
  const [nomeUsuarioLogado, setNomeUsuarioLogado] = useState('');

  // ── Guarda de acesso: restrito a Manager e Gerente/Coordenador (NÃO Admin) ──
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (!token || isExpired(token)) {
      window.location.href = '/login';
      return;
    }
    const decoded = decodeToken(token);
    const role = (decoded?.tipoUsuario || '').toUpperCase();
    setNomeUsuarioLogado(decoded?.nomeUsuario || decoded?.nome || decoded?.sub || '');
    const permitido = ['MANAGER', 'GERENTE'].includes(role);
    if (!permitido) {
      navigate('/home');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tipoAtivo, setTipoAtivo] = useState('MEMORANDO');
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemExpandidoId, setItemExpandidoId] = useState(null);

  // ── Filtro por palavra-chave — busca em número, título, descrição e anotação ──
  const [termoBusca, setTermoBusca] = useState('');

  const [novoItem, setNovoItem] = useState({ numero: '', titulo: '', descricao: '', anotacao: '' });

  const [editandoId, setEditandoId] = useState(null);
  const [edicao, setEdicao] = useState({ numero: '', titulo: '', descricao: '', anotacao: '' });

  const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

  const carregarItens = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/${ROTA_BASE}`, {
        ...getAuthHeader(),
        params: { tipo: tipoAtivo },
      });
      setItens(response.data || []);
    } catch (err) {
      setItens([]);
      addToast('error', 'Erro ao carregar registros: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL, tipoAtivo]);

  useEffect(() => { carregarItens(); }, [carregarItens]);

  const tipoInfo = TIPOS.find(t => t.id === tipoAtivo);

  // ── Filtra a lista pelo termo digitado, procurando no número formatado
  // (ex: "007/2026"), título, descrição e anotação — case-insensitive.
  // É reativo: filtra a cada tecla digitada, sem precisar de Enter/botão. ──
  const itensFiltrados = itens.filter(item => {
    const termo = termoBusca.trim().toLowerCase();
    if (!termo) return true;
    const numeroFormatado = formatarNumeroDoc(item.tipo || tipoAtivo, item.numero, item.ano).toLowerCase();
    const campos = [
      numeroFormatado,
      item.titulo || '',
      item.descricao || '',
      item.anotacao || '',
    ].join(' ').toLowerCase();
    return campos.includes(termo);
  });


  const adicionarItem = async () => {
    const numero = parseInt(novoItem.numero, 10);
    if (isNaN(numero) || numero <= 0) {
      addToast('warning', 'Informe o número do documento');
      return;
    }
    if (!novoItem.titulo.trim()) {
      addToast('warning', 'Informe o título');
      return;
    }
    try {
      const response = await axios.post(`${API_BASE_URL}/${ROTA_BASE}`, {
        tipo: tipoAtivo,
        numero,
        ano: anoAtual,
        titulo: novoItem.titulo.trim(),
        descricao: novoItem.descricao.trim(),
        anotacao: novoItem.anotacao,
      }, getAuthHeader());
      // ── O back já devolve "dataCadastro" e "nomeUsuarioCadastro" no objeto
      // criado — usamos o retorno dele como fonte de verdade, e só usamos os
      // valores locais (usuário logado + agora) como fallback, caso o back
      // não devolva o objeto completo por algum motivo. ──
      const item = {
        id: Date.now(),
        tipo: tipoAtivo,
        numero,
        ano: anoAtual,
        titulo: novoItem.titulo.trim(),
        descricao: novoItem.descricao.trim(),
        anotacao: novoItem.anotacao,
        dataCadastro: new Date().toISOString(),
        nomeUsuarioCadastro: nomeUsuarioLogado || null,
        ...(response.data || {}),
      };
      setItens(prev => [item, ...prev]);
      setNovoItem({ numero: '', titulo: '', descricao: '', anotacao: '' });
      addToast('success', `${formatarNumeroDoc(tipoAtivo, numero, anoAtual)} registrado!`);
    } catch (err) {
      addToast('error', 'Erro ao adicionar: ' + (err.response?.data?.message || err.message));
    }
  };

  const removerItem = async (id) => {
    const confirmar = window.confirm('Remover este registro definitivamente? Essa ação não pode ser desfeita.');
    if (!confirmar) return;
    try {
      await axios.delete(`${API_BASE_URL}/${ROTA_BASE}/${id}`, getAuthHeader());
      setItens(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      addToast('error', 'Erro ao remover: ' + (err.response?.data?.message || err.message));
    }
  };

  const iniciarEdicao = (item) => {
    setEditandoId(item.id);
    setEdicao({
      numero: item.numero,
      titulo: item.titulo,
      descricao: item.descricao || '',
      anotacao: item.anotacao || '',
    });
  };
  const cancelarEdicao = () => setEditandoId(null);
  const salvarEdicao = async (id) => {
    const numero = parseInt(edicao.numero, 10);
    if (isNaN(numero) || numero <= 0 || !edicao.titulo.trim()) {
      addToast('warning', 'Informe o número e o título');
      return;
    }
    const patch = {
      numero,
      titulo: edicao.titulo.trim(),
      descricao: edicao.descricao.trim(),
      anotacao: edicao.anotacao,
    };
    setItens(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));
    try {
      await axios.put(`${API_BASE_URL}/${ROTA_BASE}/${id}`, patch, getAuthHeader());
    } catch (err) {
      addToast('error', 'Erro ao atualizar: ' + (err.response?.data?.message || err.message));
    }
    setEditandoId(null);
  };

  const toggleExpandido = (id) => {
    setItemExpandidoId(prev => (prev === id ? null : id));
  };

  return (
    <div style={s.page}>
      <Header />
      <div style={s.wrap}>

        <div style={s.pageHeader}>
          <div>
            <h1 style={s.title}>Ofícios/Memorandos</h1>
            <p style={s.subtitle}>Registro numerado de memorandos, ofícios e EPROD</p>
          </div>
        </div>

        {/* ── Abas ── */}
        <div style={s.tabsBar}>
          {TIPOS.map(tipo => {
            const Icon = tipo.icon;
            const ativo = tipoAtivo === tipo.id;
            return (
              <button
                key={tipo.id}
                onClick={() => { setTipoAtivo(tipo.id); setTermoBusca(''); }}
                style={{
                  ...s.tabBtn,
                  ...(ativo ? { background: tipo.cor, color: '#fff', border: 'none' } : {}),
                }}
              >
                <Icon size={13} /> {tipo.label}
              </button>
            );
          })}
        </div>

        {/* ── Formulário de novo registro ── */}
        <div style={{ ...s.card, borderTop: `4px solid ${tipoInfo?.cor}` }}>
          <div style={s.cardHeader}>
            <span style={s.cardHeaderTitle}>Novo {tipoInfo?.label.replace(/s$/, '')}</span>
          </div>
          <div style={s.formGrid}>
            <div style={s.campoGrupo}>
              <label style={s.campoLabel}>Número</label>
              <input
                type="number"
                min="1"
                value={novoItem.numero}
                onChange={(e) => setNovoItem(prev => ({ ...prev, numero: e.target.value }))}
                placeholder="Ex: 7"
                style={s.input}
              />
              {novoItem.numero && !isNaN(parseInt(novoItem.numero, 10)) && (
                <span style={{ ...s.previewNumero, color: tipoInfo?.cor }}>
                  {formatarNumeroDoc(tipoAtivo, parseInt(novoItem.numero, 10), anoAtual)}
                </span>
              )}
            </div>
            <div style={{ ...s.campoGrupo, flex: 2, minWidth: 220 }}>
              <label style={s.campoLabel}>Título</label>
              <input
                type="text"
                value={novoItem.titulo}
                onChange={(e) => setNovoItem(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Título do documento"
                style={s.input}
              />
            </div>
          </div>
          <div style={{ ...s.campoGrupo, marginTop: 12 }}>
            <label style={s.campoLabel}>Descrição</label>
            <input
              type="text"
              value={novoItem.descricao}
              onChange={(e) => setNovoItem(prev => ({ ...prev, descricao: e.target.value }))}
              placeholder="Breve descrição do assunto"
              style={s.input}
            />
          </div>
          <div style={{ ...s.campoGrupo, marginTop: 12 }}>
            <label style={s.campoLabel}>Anotação</label>
            <textarea
              value={novoItem.anotacao}
              onChange={(e) => setNovoItem(prev => ({ ...prev, anotacao: e.target.value }))}
              placeholder="Texto completo que ficará registrado..."
              style={s.textarea}
              rows={4}
            />
          </div>
          <button onClick={adicionarItem} style={{ ...s.btnAdd, background: tipoInfo?.cor, marginTop: 14 }}>
            <FaPlus size={11} /> Adicionar {tipoInfo?.label.replace(/s$/, '')}
          </button>
        </div>

        {/* ── Filtro por palavra-chave: reativo, filtra a cada tecla —
            não precisa de Enter nem de um botão "Filtrar" separado ── */}
        <div style={s.buscaBox}>
          <FaSearch size={13} style={{ color: '#aaa', flexShrink: 0 }} />
          <input
            type="text"
            value={termoBusca}
            onChange={(e) => setTermoBusca(e.target.value)}
            placeholder={`Buscar por número, título, descrição ou anotação em ${tipoInfo?.label.toLowerCase()}...`}
            style={s.buscaInput}
          />
          {termoBusca && (
            <button onClick={() => setTermoBusca('')} style={s.buscaClear}><FaTimes size={11} /></button>
          )}
        </div>
        {termoBusca.trim() && (
          <p style={s.hintTexto}>
            {itensFiltrados.length} resultado{itensFiltrados.length === 1 ? '' : 's'} para "{termoBusca.trim()}"
          </p>
        )}

        {/* ── Lista de registros ── */}
        <div style={s.lista}>
          {loading && <p style={s.hintTexto}>Carregando...</p>}
          {!loading && itens.length === 0 && (
            <div style={s.emptyState}>
              <FaFileAlt size={28} color="#ddd" />
              <p>Nenhum {tipoInfo?.label.toLowerCase().replace(/s$/, '')} registrado ainda.</p>
            </div>
          )}
          {!loading && itens.length > 0 && itensFiltrados.length === 0 && (
            <div style={s.emptyState}>
              <FaSearch size={28} color="#ddd" />
              <p>Nenhum resultado encontrado para "{termoBusca.trim()}".</p>
            </div>
          )}
          {itensFiltrados.map(item => {
            // ── Durante uma busca, expande automaticamente os resultados —
            // assim o usuário já vê onde a palavra-chave apareceu (descrição
            // ou anotação) sem precisar clicar item por item ──
            const expandido = termoBusca.trim() ? true : itemExpandidoId === item.id;
            const editando = editandoId === item.id;
            const criador = nomeCriador(item);
            const dataCadastroItem = dataRegistro(item);
            return (
              <div key={item.id} style={{ ...s.itemCard, borderLeft: `4px solid ${tipoInfo?.cor}` }}>
                <div style={s.itemHeader}>
                  <div style={s.itemHeaderLeft} onClick={() => !editando && toggleExpandido(item.id)}>
                    <span style={{ ...s.itemNumero, color: tipoInfo?.cor }}>
                      {formatarNumeroDoc(item.tipo || tipoAtivo, item.numero, item.ano)}
                    </span>
                    {editando ? (
                      <input
                        type="text"
                        value={edicao.titulo}
                        onChange={(e) => setEdicao(prev => ({ ...prev, titulo: e.target.value }))}
                        style={s.inputInline}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <span style={s.itemTitulo}>{item.titulo}</span>
                    )}
                  </div>

                  {/* ── Quem criou o registro + quando — visível direto
                      na linha do item, sem precisar expandir ── */}
                  {!editando && (
                    <span style={s.itemHeaderMeta} title={dataCadastroItem ? formatarDataHoraBR(dataCadastroItem) : undefined}>
                      <FaUser size={9} style={{ flexShrink: 0 }} />
                      {criador ? `${criador} · ` : ''}{formatarDataHoraBR(dataCadastroItem) || '—'}
                    </span>
                  )}

                  <div style={s.itemHeaderActions}>
                    {editando ? (
                      <>
                        <button onClick={() => salvarEdicao(item.id)} style={s.btnSalvarInline}><FaCheck size={11} /></button>
                        <button onClick={cancelarEdicao} style={s.btnCancelarInline}><FaTimes size={11} /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => iniciarEdicao(item)} style={s.btnEditInline}><FaEdit size={11} /></button>
                        <button onClick={() => removerItem(item.id)} style={s.btnDelete}><FaTrash size={11} /></button>
                        <button onClick={() => toggleExpandido(item.id)} style={s.btnExpand}>
                          {expandido ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {editando ? (
                  <div style={s.itemBody}>
                    <div style={s.campoGrupo}>
                      <label style={s.campoLabel}>Número</label>
                      <input
                        type="number"
                        min="1"
                        value={edicao.numero}
                        onChange={(e) => setEdicao(prev => ({ ...prev, numero: e.target.value }))}
                        style={s.input}
                      />
                    </div>
                    <div style={{ ...s.campoGrupo, marginTop: 10 }}>
                      <label style={s.campoLabel}>Descrição</label>
                      <input
                        type="text"
                        value={edicao.descricao}
                        onChange={(e) => setEdicao(prev => ({ ...prev, descricao: e.target.value }))}
                        style={s.input}
                      />
                    </div>
                    <div style={{ ...s.campoGrupo, marginTop: 10 }}>
                      <label style={s.campoLabel}>Anotação</label>
                      <textarea
                        value={edicao.anotacao}
                        onChange={(e) => setEdicao(prev => ({ ...prev, anotacao: e.target.value }))}
                        style={s.textarea}
                        rows={4}
                      />
                    </div>
                  </div>
                ) : (
                  expandido && (
                    <div style={s.itemBody}>
                      {item.descricao && <p style={s.itemDescricao}>{item.descricao}</p>}
                      <div style={s.itemAnotacaoBox}>
                        <span style={s.itemAnotacaoLabel}>Anotação</span>
                        <p style={s.itemAnotacaoTexto}>{item.anotacao || '—'}</p>
                      </div>
                      {dataCadastroItem && (
                        <span style={s.itemRegistradoEm}>
                          Registrado {criador ? `por ${criador} ` : ''}em {formatarDataHoraBR(dataCadastroItem)}
                        </span>
                      )}
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Estilos ─────────────────────────────────────────────────────────────────
const s = {
  page: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f0f4f8', fontFamily: "'Segoe UI', sans-serif" },
  wrap: { maxWidth: 1000, margin: '0 auto', width: '100%', padding: '20px 24px', boxSizing: 'border-box' },

  pageHeader: { marginBottom: 16 },
  title: { margin: 0, fontSize: 22, fontWeight: 700, color: '#1a2e35' },
  subtitle: { margin: '4px 0 0', fontSize: 12, color: '#7f8c8d' },

  tabsBar: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  tabBtn: { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: '#fff', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#555' },

  buscaBox: { display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 14px', marginBottom: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  buscaInput: { flex: 1, border: 'none', outline: 'none', fontSize: 13, background: 'transparent', color: '#333' },
  buscaClear: { background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', display: 'flex', alignItems: 'center', padding: 2 },

  card: { background: '#fff', borderRadius: 10, padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 20 },
  cardHeader: { marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid #f0f0f0' },
  cardHeaderTitle: { fontSize: 14, fontWeight: 700, color: '#2c3e50' },

  formGrid: { display: 'flex', gap: 14, flexWrap: 'wrap' },
  campoGrupo: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 130 },
  campoLabel: { fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 },
  input: { padding: '9px 11px', border: '1.5px solid #e0e0e0', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  inputInline: { padding: '5px 8px', border: '1.5px solid #008080', borderRadius: 6, fontSize: 13, flex: 1 },
  textarea: { padding: '9px 11px', border: '1.5px solid #e0e0e0', borderRadius: 7, fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' },
  previewNumero: { fontSize: 11.5, fontWeight: 700, marginTop: 2 },

  btnAdd: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12.5, fontWeight: 600 },

  lista: { display: 'flex', flexDirection: 'column', gap: 10 },
  hintTexto: { fontSize: 12, color: '#aaa', fontStyle: 'italic' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '40px 20px', background: '#fff', borderRadius: 10, color: '#aaa', fontSize: 13 },

  itemCard: { background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' },
  itemHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', gap: 10 },
  itemHeaderLeft: { display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, cursor: 'pointer' },
  itemNumero: { fontSize: 12.5, fontWeight: 800, whiteSpace: 'nowrap' },
  itemTitulo: { fontSize: 13.5, fontWeight: 600, color: '#2c3e50', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  // ── "quem criou + quando", visível na linha do item sem precisar expandir ──
  itemHeaderMeta: {
    display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
    fontSize: 11, color: '#999', whiteSpace: 'nowrap',
  },

  itemHeaderActions: { display: 'flex', gap: 4, flexShrink: 0 },
  btnEditInline: { background: 'none', border: 'none', cursor: 'pointer', color: '#3498db', padding: 6, display: 'flex', alignItems: 'center' },
  btnSalvarInline: { background: 'none', border: 'none', cursor: 'pointer', color: '#27ae60', padding: 6, display: 'flex', alignItems: 'center' },
  btnCancelarInline: { background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', padding: 6, display: 'flex', alignItems: 'center' },
  btnDelete: { background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', padding: 6, display: 'flex', alignItems: 'center' },
  btnExpand: { background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: 6, display: 'flex', alignItems: 'center' },

  itemBody: { padding: '0 16px 16px 16px', borderTop: '1px solid #f5f5f5', display: 'flex', flexDirection: 'column', gap: 10 },
  itemDescricao: { fontSize: 12.5, color: '#555', margin: '10px 0 0 0' },
  itemAnotacaoBox: { background: '#f8fbfb', border: '1px solid #e8e8e8', borderRadius: 7, padding: '10px 12px' },
  itemAnotacaoLabel: { fontSize: 10.5, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 },
  itemAnotacaoTexto: { fontSize: 13, color: '#2c3e50', margin: '6px 0 0 0', whiteSpace: 'pre-wrap', lineHeight: 1.5 },
  itemRegistradoEm: { fontSize: 11, color: '#aaa', fontStyle: 'italic' },
};

export default OficiosMemorandos;
